import { JSX } from "react";
import { Component } from "common/component";
import { TableMap, TableValue } from "common/query/table/types";
import { QueryForEntity, RunQuery, RunQueryWithSite } from "common/query/types";
import { Context } from "common/types/context";
import { CancellablePromise } from "common/types/promises";
import { Properties } from "common/types/records";
import { FullPageModal } from "common/widgets/full-page-modal";
// eslint-disable-next-line import/no-cycle
import { LazyAdvancedSearchContent } from "common/widgets/record-selector/advanced-search/modal";
import { VerticalField } from "common/ui/field";
import { RadioButtonList } from "common/widgets/radio-button-list";
import { SearchContentProps } from "../types";

interface PropTypes {
  selectProperty: (newSelected: Properties[]) => void;
  context: Context;
  runQuery: RunQueryWithSite;
  content: SearchContentProps[];
  withLinks: boolean;
  disabled?: boolean;
  allowMultipleSelect?: boolean;
  allowAdvancedAdding?: boolean;
  multiSearchContentTitle?: string;
  approveChange?: () => CancellablePromise<void>;
}

interface MappedSearchContentProps {
  label: string;
  value: number;
}

interface State {
  showModal?: boolean;
  tables: TableMap;
  selectedContent: number;
}

export class AdvancedSearch extends Component<PropTypes, State> {
  static readonly displayName = "AdvancedSearch";
  state: State = {
    showModal: false,
    tables: undefined,
    selectedContent: 0,
  };

  selectProperties = (properties: Properties[]) => {
    const { selectProperty } = this.props;
    selectProperty(properties);
    this.setState({ showModal: false });
  };

  onActionSelect = (newSelected: Properties) => {
    const { approveChange } = this.props;

    if (approveChange) {
      approveChange()
        .then(() => this.selectProperties([newSelected]))
        .catch(() => {
          /* ignore */
        });
    } else {
      this.selectProperties([newSelected]);
    }
  };

  onModifyButtonClick = () => {
    const { tables = [] } = this.state;
    this.selectProperties(tables[0]?.selected);
  };

  onAdvancedSearchClick = () => {
    this.setState({ showModal: true, tables: undefined });
  };

  onSearchContentChange =
    (index: number = 0) =>
    (tableValue: TableValue) => {
      const { tables = [] } = this.state;

      const newTables = { ...tables, [index]: tableValue };
      this.setState({ tables: newTables });
    };

  getSearchTypeDisplayLabel = (content: MappedSearchContentProps): string => {
    return content.label;
  };

  onSearchTypeChange = (content: MappedSearchContentProps) => {
    this.setState({ selectedContent: content.value });
  };

  getSelectedSearchType = (
    searchContents: MappedSearchContentProps[],
  ): MappedSearchContentProps => {
    const { selectedContent } = this.state;
    return searchContents.find((content) => content.value === selectedContent);
  };

  runQuery =
    (site: string): RunQuery =>
    (query: QueryForEntity) =>
      this.props.runQuery(query, site);

  render(): JSX.Element {
    const {
      runQuery,
      content,
      disabled,
      allowMultipleSelect,
      context,
      withLinks,
      allowAdvancedAdding,
      multiSearchContentTitle,
    } = this.props;
    const { showModal, tables = [], selectedContent } = this.state;
    const newQuery = tables[0]?.query || content[0].queryForEntity;

    const iconClass = newQuery
      ? allowAdvancedAdding
        ? "fa-plus-circle"
        : "fa-search"
      : "fa-spinner fa-spin";

    const multiSearchContents: MappedSearchContentProps[] = content.map(
      (searchContent, index) => ({
        label: searchContent.label,
        value: index,
      }),
    );

    return (
      <div className="x-advanced-search">
        <button
          className="btn qa-advanced-search-btn"
          title={_("Advanced search")}
          disabled={!newQuery || disabled}
          onClick={this.onAdvancedSearchClick}
        >
          <i className={`fa ${iconClass}`} />
        </button>
        {newQuery && showModal && (
          <FullPageModal
            title={_("Advanced search")}
            closeButtonClick={this.onChangeMergeDefaultState(
              "showModal",
              false,
            )}
            modifyButtonClick={allowMultipleSelect && this.onModifyButtonClick}
          >
            {content.length > 1 ? (
              <div className="x-advanced-search-radio-buttons">
                <VerticalField
                  className="x-select-field"
                  label={multiSearchContentTitle || _("Select Options")}
                  input={
                    <RadioButtonList
                      name="searchType"
                      display={this.getSearchTypeDisplayLabel}
                      items={multiSearchContents}
                      value={this.getSelectedSearchType(multiSearchContents)}
                      onChange={this.onSearchTypeChange}
                    />
                  }
                />
                {content.map((searchContent, index) => {
                  const { queryForEntity, value, site } = searchContent;
                  const table = tables[index];
                  return (
                    selectedContent === index && (
                      <LazyAdvancedSearchContent
                        key={value}
                        runQuery={this.runQuery(site)}
                        query={table?.query || queryForEntity}
                        table={table}
                        context={context}
                        withLinks={withLinks}
                        allowMultipleSelect={allowMultipleSelect}
                        onChange={this.onSearchContentChange(index)}
                        onSelect={this.onActionSelect}
                      />
                    )
                  );
                })}
              </div>
            ) : (
              <LazyAdvancedSearchContent
                runQuery={runQuery}
                query={newQuery}
                table={tables[0]}
                context={context}
                withLinks={withLinks}
                allowMultipleSelect={allowMultipleSelect}
                onChange={this.onSearchContentChange()}
                onSelect={this.onActionSelect}
              />
            )}
          </FullPageModal>
        )}
      </div>
    );
  }
}
